import React, { useCallback, useEffect, useState } from 'react';
import { LuLoaderPinwheel, LuMessageSquare, LuPlus, LuSparkles } from 'react-icons/lu';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { ChatSession, createSession, getProjectSessions } from '../../shared/api/chat-sessions';
import { useProjects, useProjectsMeta } from '../../shared/contexts/Projects.provider';
import { auth } from '../../shared/firebase/firebase';
import { useStandaloneProject } from '../../shared/hooks/use-standalone-project';
import { useStandaloneProjects } from '../../shared/hooks/use-standalone-projects';
import { useTrelloBoards } from '../../shared/hooks/use-trello-boards';
import {
  isStandaloneProjectId,
  resolveApiProjectId,
  STUDIO_PROJECT_ID,
  VIRTUAL_PROJECTS,
} from '../../shared/utils/virtual-projects';

const ProjectSessionList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const projects = useProjects();
  const { isReady } = useProjectsMeta();
  const { boards: trelloBoards } = useTrelloBoards();
  const { project: standaloneProject } = useStandaloneProject();
  const { projects: standaloneProjects } = useStandaloneProjects();
  const userId = auth.currentUser?.uid ?? '';

  const [selectedProjectId, setSelectedProjectId] = useState<string>(searchParams.get('projectId') ?? '');
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isStartingGeneral, setIsStartingGeneral] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSelectProject = useCallback(
    (id: string) => {
      setSelectedProjectId(id);
      setSearchParams({ projectId: id }, { replace: true });
    },
    [setSearchParams],
  );

  // Auto-select first project (or the first virtual entry, if none) once loaded — only if nothing in URL
  useEffect(() => {
    if (!isReady || selectedProjectId) return;
    const firstId = projects[0]?._id ?? projects[0]?.id ?? VIRTUAL_PROJECTS[0].id;
    handleSelectProject(firstId);
  }, [isReady, projects, selectedProjectId, handleSelectProject]);

  // Standalone has no discovery endpoint — once bound (see use-standalone-project), route session
  // create/list calls to its real id instead of the shared bootstrap bucket.
  const resolvedSelectedProjectId =
    isStandaloneProjectId(selectedProjectId) && standaloneProject
      ? standaloneProject._id
      : resolveApiProjectId(selectedProjectId, userId);

  // Fetch sessions when project changes
  useEffect(() => {
    if (!resolvedSelectedProjectId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    getProjectSessions(resolvedSelectedProjectId)
      .then((list) => {
        if (cancelled) return;
        setSessions(list);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load sessions.');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedSelectedProjectId]);

  const handleNewSession = async () => {
    if (!selectedProjectId) return;
    setIsCreating(true);
    try {
      const virtual = VIRTUAL_PROJECTS.find((v) => v.id === selectedProjectId);
      const session = await createSession(resolvedSelectedProjectId, virtual ? `${virtual.label} session` : undefined);
      setSessions((prev) => [session, ...prev]);
      navigate(`/chat/sessions/${session._id}?projectId=${selectedProjectId}`);
    } catch {
      setError('Failed to create session.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenSession = (session: ChatSession) => {
    navigate(`/chat/sessions/${session._id}?projectId=${selectedProjectId}`);
  };

  // Skips the "pick a project first" flow entirely — creates a studio (general chat) session
  // directly. This is the answer to new users landing on a forced project dropdown before they
  // even know what a "project" means here; a project can be attached to the conversation later.
  const handleStartGeneralChat = async () => {
    setIsStartingGeneral(true);
    try {
      const session = await createSession(resolveApiProjectId(STUDIO_PROJECT_ID, userId), 'General chat');
      navigate(`/chat/sessions/${session._id}?projectId=${STUDIO_PROJECT_ID}`);
    } catch {
      setError('Failed to start a new chat.');
    } finally {
      setIsStartingGeneral(false);
    }
  };

  const hasRealProjects = projects.length > 0 || trelloBoards.length > 0 || standaloneProjects.length > 0;

  const selectedProject = projects.find((p) => (p._id ?? p.id) === selectedProjectId);
  const selectedTrelloBoard = trelloBoards.find((b) => b.id === selectedProjectId);
  const selectedStandaloneProject = standaloneProjects.find((p) => p._id === selectedProjectId);
  const selectedVirtualProject = VIRTUAL_PROJECTS.find((v) => v.id === selectedProjectId);
  const isSelectedBoundStandalone = isStandaloneProjectId(selectedProjectId) && !!standaloneProject;
  const selectedProjectLabel = isSelectedBoundStandalone
    ? standaloneProject!.name
    : (selectedVirtualProject?.label ??
      selectedTrelloBoard?.name ??
      selectedStandaloneProject?.name ??
      selectedProject?.name);

  if (!isReady) {
    return (
      <div className="w-full space-y-4">
        <div className="skeleton h-12 w-full rounded-xl" />
        <div className="skeleton h-16 w-full rounded-2xl" />
        <div className="skeleton h-16 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Start a general chat — no project pick required. This is the primary entry point for a
          new user who doesn't have a mental model of "projects" here yet; one can be attached to
          the conversation later (see AttachProjectControl in SessionChatView). */}
      <div className="card bg-primary/5 shadow-glow">
        <div className="card-body p-4 gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center">
              <LuSparkles size={18} />
            </div>
            <div>
              <div className="font-semibold">Start a new chat</div>
              <div className="text-sm opacity-70">
                Ask anything — no project needed. Attach one to the conversation whenever you&apos;re ready.
              </div>
            </div>
          </div>
          <button
            className="btn btn-primary gap-2 shrink-0"
            onClick={handleStartGeneralChat}
            disabled={isStartingGeneral}
          >
            {isStartingGeneral ? <LuLoaderPinwheel className="animate-spin" size={16} /> : <LuPlus size={16} />}
            New chat
          </button>
        </div>
      </div>

      {!hasRealProjects && (
        <div className="text-xs opacity-60 px-1">
          Or{' '}
          <Link to="/integrations" className="link link-primary">
            connect Jira or Trello
          </Link>{' '}
          to start project-scoped chats too.
        </div>
      )}

      {/* Project selector */}
      <div className="card bg-base-100 shadow-card">
        <div className="card-body p-4 gap-2">
          <div className="text-xs font-semibold opacity-50 uppercase tracking-wide">Select project</div>
          <div className="flex items-center gap-3">
            <select
              className="select select-bordered flex-1"
              value={selectedProjectId}
              onChange={(e) => handleSelectProject(e.target.value)}
            >
              {projects.map((p) => {
                const id = p._id ?? p.id ?? '';
                return (
                  <option key={id} value={id}>
                    {p.name ?? id}
                  </option>
                );
              })}
              {trelloBoards.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} (Trello)
                </option>
              ))}
              {standaloneProjects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name} (Standalone)
                </option>
              ))}
              {VIRTUAL_PROJECTS.map((v) => (
                <option key={v.id} value={v.id}>
                  {isStandaloneProjectId(v.id) && standaloneProject ? standaloneProject.name : v.label}
                </option>
              ))}
            </select>

            <button
              className="btn btn-primary gap-2 shrink-0"
              onClick={handleNewSession}
              disabled={!selectedProjectId || isCreating}
            >
              {isCreating ? <LuLoaderPinwheel className="animate-spin" size={16} /> : <LuPlus size={16} />}
              New session
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      {/* Session list */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton h-16 w-full rounded-2xl" />
          ))}
        </div>
      ) : sessions.length === 0 ? (
        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-6 items-center text-center">
            <LuMessageSquare size={32} className="opacity-30" />
            <div className="text-sm opacity-60">
              {selectedProjectId
                ? `No sessions yet for ${selectedProjectLabel ?? selectedProjectId}. Start a new one.`
                : 'Select a project to see sessions.'}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {sessions.map((session) => {
            const title = session.title ?? new Date(session.createdAt).toLocaleString();
            const updated = new Date(session.updatedAt).toLocaleDateString();
            return (
              <div
                key={session._id}
                className="card bg-base-100 shadow-card hover:shadow-md transition-shadow cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => handleOpenSession(session)}
                onKeyDown={(e) => e.key === 'Enter' && handleOpenSession(session)}
              >
                <div className="card-body p-4 flex-row items-center gap-3">
                  <div className="bg-base-200 rounded-2xl p-2 shrink-0">
                    <LuMessageSquare size={18} className="opacity-60" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{title}</div>
                    <div className="text-xs opacity-50">Updated {updated}</div>
                  </div>
                  <button
                    className="btn btn-sm btn-outline shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenSession(session);
                    }}
                  >
                    Open
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectSessionList;
